'use client'

import { useEffect, useRef, useState } from 'react'
import Matter from 'matter-js'
import { Events } from '@/sanity.types'
import decomp from 'poly-decomp'
import 'pathseg'
import { useScroll } from '@/components/global/ScrollContext'
import useScreenSize from '@/src/hooks/useScreenSize'

interface EventBall {
  radius: number
  body: Matter.Body
  event: Events
  color: string
}

interface EventsPhysicsCanvasProps {
  events: Events[]
}

const EventsPhysicsCanvas = ({ events }: EventsPhysicsCanvasProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const engineRef = useRef<Matter.Engine | null>(null)
  const renderRef = useRef<Matter.Render | null>(null)
  const eventBallsRef = useRef<EventBall[]>([])
  const decorativeBallsRef = useRef<Matter.Body[]>([])
  const animationRef = useRef<number>(0)
  const [hoveredBall, setHoveredBall] = useState<string | null>(null)
  const hoveredBallRef = useRef<string | null>(null)
  const prevScrollYRef = useRef<number>(0)
  const { scrollY } = useScroll()
  const { width } = useScreenSize()

  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 })
  const [decorativeCount, setDecorativeCount] = useState(8)

  useEffect(() => {
    if (width < 768) {
      setDecorativeCount(6)
    } else {
      setDecorativeCount(15)
    }
  }, [width])

  useEffect(() => {
    const updateCanvasSize = () => {
      const container = containerRef.current
      if (!container) return

      setCanvasSize({
        width: container.clientWidth,
        height: container.clientHeight + 20,
      })
    }

    const timeout = setTimeout(() => {
      updateCanvasSize()
    }, 100)
    window.addEventListener('resize', updateCanvasSize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', updateCanvasSize)
    }
  }, [])

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current || !events?.length) return

    const {
      Engine,
      Render,
      Bodies,
      Composite,
      Mouse,
      MouseConstraint,
      Events: MatterEvents,
    } = Matter

    // enable concave decomposition for SVG vertices
    ;(Matter as any).Common?.setDecomp?.(decomp)

    // 엔진 생성
    const engine = Engine.create({})
    engine.world.gravity.y = 0.5
    engine.world.gravity.scale = 0.001

    // 렌더러 생성 (캔버스에 모든 것을 렌더링)
    const render = Render.create({
      canvas: canvasRef.current,
      engine: engine,
      options: {
        width: canvasSize.width,
        height: canvasSize.height,
        wireframes: false,
        background: 'transparent',
        showAngleIndicator: false,
        showDebug: false,
        showVelocity: false,
      },
    })

    // 고해상도 캔버스 렌더링 품질 개선
    try {
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      ;(Matter.Render as any).setPixelRatio?.(render, dpr)
    } catch {}

    // 경계 벽 생성
    const wallThickness = 50
    const walls = [
      Bodies.rectangle(
        canvasSize.width / 2,
        canvasSize.height + wallThickness / 2,
        canvasSize.width,
        wallThickness,
        { isStatic: true, render: { visible: false } }
      ),
      Bodies.rectangle(
        -wallThickness / 2,
        canvasSize.height / 2,
        wallThickness,
        canvasSize.height,
        { isStatic: true, render: { visible: false } }
      ),
      Bodies.rectangle(
        canvasSize.width + wallThickness / 2,
        canvasSize.height / 2,
        wallThickness,
        canvasSize.height,
        { isStatic: true, render: { visible: false } }
      ),
      Bodies.rectangle(canvasSize.width / 2, -wallThickness / 2, canvasSize.width, wallThickness, {
        isStatic: true,
        render: { visible: false },
      }),
    ]

    // 이벤트 볼들 생성 (SVG 기반)
    const eventBalls: EventBall[] = []
    const decorativeBalls: Matter.Body[] = []

    const svgColors = ['#03c75a', '#50b3f9', '#ff8000']

    // SVG 로드 및 이벤트 바디 생성
    const loadSvgAndCreateBodies = async () => {
      try {
        const response = await fetch('/images/event-big.svg')
        const raw = await response.text()
        const doc = new window.DOMParser().parseFromString(raw, 'image/svg+xml')

        const paths = Array.from(doc.querySelectorAll('path'))
        const Svg: any = (Matter as any).Svg
        const Vertices: any = (Matter as any).Vertices

        // 원본 버텍스 세트를 추출
        const vertexSetsBase = paths.map(p => Svg.pathToVertices(p, 10)) as {
          x: number
          y: number
        }[][]

        // 전체 경계 폭 계산
        let minX = Infinity
        let maxX = -Infinity
        vertexSetsBase.forEach(set => {
          set.forEach(v => {
            if (v.x < minX) minX = v.x
            if (v.x > maxX) maxX = v.x
          })
        })
        const baseWidth = Math.max(1, maxX - minX)

        events.forEach((event, index) => {
          if (!event.title) return

          const x = Math.random() * (canvasSize.width - 200) + 100
          const y = Math.random() * 150 + 100
          // 랜덤 크기 제거: 고정 크기
          const radius = 50

          const targetWidth = 200
          const scale = targetWidth / baseWidth

          // Vertices.scale mutates in-place; clone before scaling to avoid cumulative scaling across events
          const scaledVertexSets = vertexSetsBase.map(set => {
            const clone = set.map((v: any) => ({ x: v.x, y: v.y }))
            return Vertices.scale(clone, scale, scale)
          })

          const color = svgColors[index % svgColors.length]

          const body = Bodies.fromVertices(
            x,
            y,
            scaledVertexSets as unknown as Matter.Vector[][],
            {
              restitution: 0.45,
              friction: 0.12,
              frictionAir: 0.01,
              density: 3,
              render: {
                fillStyle: color,
                strokeStyle: color,
                lineWidth: 0,
              },
            },
            true
          ) as Matter.Body

          body.label = `event_${event._id}`

          const eventBall: EventBall = {
            radius,
            body,
            event,
            color,
          }

          eventBalls.push(eventBall)
        })

        // SVG 기반 이벤트 바디를 월드에 추가
        Composite.add(
          engine.world,
          eventBalls.map(eb => eb.body)
        )
      } catch (e) {
        // SVG 로드 실패 시 원형으로 폴백
        events.forEach((event, index) => {
          if (!event.title) return

          const x = Math.random() * (canvasSize.width - 200) + 100
          const y = Math.random() * 150 + 50
          const radius = 50
          const color = svgColors[index % svgColors.length]

          const body = Bodies.circle(x, y, radius, {
            restitution: 0.5,
            friction: 0.32,
            frictionAir: 0.03,
            density: 1.2,
            render: { fillStyle: color },
          })
          body.label = `event_${event._id}`
          eventBalls.push({ radius, body, event, color })
        })
        Composite.add(
          engine.world,
          eventBalls.map(eb => eb.body)
        )
      }

      // 참조 저장 (SVG 로드 이후에도 최신 상태 반영)
      eventBallsRef.current = eventBalls
    }

    // 장식용 볼들 생성 (더 작고 다양한 색상)

    const loadSmallSvgAndCreateDecoratives = async () => {
      try {
        const response = await fetch('/images/event-small.svg')
        const raw = await response.text()
        const doc = new window.DOMParser().parseFromString(raw, 'image/svg+xml')
        const paths = Array.from(doc.querySelectorAll('path'))
        const Svg: any = (Matter as any).Svg
        const Vertices: any = (Matter as any).Vertices

        const vertexSetsBase = paths.map(p => Svg.pathToVertices(p, 10)) as {
          x: number
          y: number
        }[][]

        let minX = Infinity
        let maxX = -Infinity
        vertexSetsBase.forEach(set => {
          set.forEach(v => {
            if (v.x < minX) minX = v.x
            if (v.x > maxX) maxX = v.x
          })
        })
        const baseWidth = Math.max(1, maxX - minX)

        const targetWidth = 80
        const scale = targetWidth / baseWidth
        const scaledVertexSets = vertexSetsBase.map(set => Vertices.scale(set, scale, scale))

        for (let i = 0; i < decorativeCount; i++) {
          const x = Math.random() * canvasSize.width + 50
          const y = Math.random() * 400 + 50
          const color = '#ffffff'
          const body = Bodies.fromVertices(
            x,
            y,
            scaledVertexSets as unknown as Matter.Vector[][],
            {
              restitution: 0.9,
              friction: 0.0001,
              frictionAir: 0.0001,
              density: 0.0005,
              render: {
                fillStyle: color,
                strokeStyle: color,
                lineWidth: 0,
              },
            },
            true
          ) as Matter.Body
          body.label = `decorative_${i}`
          decorativeBalls.push(body)
        }

        Composite.add(engine.world, decorativeBalls)
        decorativeBallsRef.current = decorativeBalls
      } catch {}
    }

    // 월드에 벽 추가 (이벤트/장식 도형은 SVG 로드 후 추가)
    Composite.add(engine.world, walls)

    // SVG 로드 시작
    loadSvgAndCreateBodies()
    loadSmallSvgAndCreateDecoratives()

    // 마우스 컨트롤 추가
    const mouse = Mouse.create(render.canvas)
    const mouseConstraint = MouseConstraint.create(engine, {
      mouse: mouse,
      constraint: {
        stiffness: 0.2,
        render: {
          visible: false,
        },
      },
    })
    Composite.add(engine.world, mouseConstraint)

    // 호버 및 클릭 이벤트 처리
    MatterEvents.on(mouseConstraint, 'mousemove', event => {
      const mousePosition = event.mouse.position
      let foundHover = false

      for (const eventBall of eventBalls) {
        const body = eventBall.body
        const hit = Matter.Query.point([body], mousePosition)
        if (hit.length > 0) {
          setHoveredBall(eventBall.event._id)
          hoveredBallRef.current = eventBall.event._id
          render.canvas.style.cursor = 'pointer'
          foundHover = true
          break
        }
      }

      if (!foundHover) {
        setHoveredBall(null)
        hoveredBallRef.current = null
        render.canvas.style.cursor = 'default'
      }
    })

    // 클릭 이벤트 처리
    MatterEvents.on(mouseConstraint, 'mousedown', event => {
      const mousePosition = event.mouse.position

      for (const eventBall of eventBalls) {
        const body = eventBall.body
        const hit = Matter.Query.point([body], mousePosition)
        if (hit.length > 0) {
          const elementId = (eventBall.event as any).slug?.current || eventBall.event._id
          const eventElement = document.getElementById(elementId)
          if (eventElement) {
            eventElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          }
          break
        }
      }
    })

    // 캔버스에 텍스트 렌더링을 위한 afterRender 이벤트
    MatterEvents.on(render, 'afterRender', () => {
      const context = render.canvas.getContext('2d')
      if (!context) return

      eventBalls.forEach(eventBall => {
        const { x, y } = eventBall.body.position
        const title = eventBall.event.title || ''
        const text = title.length > 12 ? title.slice(0, 12) + '...' : title

        // 텍스트 스타일 설정
        context.save()
        context.translate(x, y)
        context.rotate(eventBall.body.angle)

        const isHovered = hoveredBallRef.current === eventBall.event._id
        const fontSize = isHovered ? 20 : 20
        context.font = `900 ${fontSize}px mgothic-210`
        context.fillStyle = isHovered ? '#000000' : '#000000'
        context.textAlign = 'center'
        context.textBaseline = 'middle'

        // 텍스트 아웃라인과 텍스트 그리기
        // context.strokeText(text, 0, 0)
        context.fillText(text, 0, 0)

        context.restore()
      })
    })

    // 렌더링 시작
    Render.run(render)

    // Engine 업데이트를 수동으로 처리 (사용자가 제안한 방식)
    let lastTime = 0
    function updateEngine(currentTime: number) {
      if (lastTime === 0) {
        lastTime = currentTime
        Engine.update(engine, 16.67)
        return
      }
      const deltaTime = currentTime - lastTime
      lastTime = currentTime
      const clamped = Math.min(16.67, deltaTime || 16.67)
      Engine.update(engine, clamped)
    }

    // 물리 엔진 업데이트를 위한 애니메이션 루프
    function updatePhysics(currentTime: number) {
      updateEngine(currentTime)
      animationRef.current = requestAnimationFrame(updatePhysics)
    }

    animationRef.current = requestAnimationFrame(updatePhysics)

    // 참조 저장
    engineRef.current = engine
    renderRef.current = render
    eventBallsRef.current = eventBalls
    decorativeBallsRef.current = decorativeBalls

    // 정리 함수
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      if (renderRef.current) {
        Render.stop(renderRef.current)
        // 렌더러만 중지. 캔버스 DOM은 제거하지 않음 (React가 관리)
      }
      if (engineRef.current) {
        Engine.clear(engineRef.current)
      }

      eventBallsRef.current = []
      decorativeBallsRef.current = []
    }
  }, [events, canvasSize])

  useEffect(() => {
    const engine = engineRef.current
    if (!engine) return

    const previous = prevScrollYRef.current
    const delta = scrollY - previous
    prevScrollYRef.current = scrollY
    if (delta === 0) return

    const bodies: Matter.Body[] = [
      ...eventBallsRef.current.map(e => e.body),
      ...decorativeBallsRef.current,
    ]
    if (bodies.length === 0) return

    // Treat magnitude as acceleration; convert to force by multiplying with mass
    const baseMagnitude = Math.min(0.004, Math.max(0.0004, Math.abs(delta) * 0.0004))
    for (const body of bodies) {
      const isEvent = typeof body.label === 'string' && body.label.startsWith('event_')
      const factor = isEvent ? 0.5 : 1
      const magnitude = baseMagnitude * factor
      const jitterX = (Math.random() - 0.5) * magnitude * 0.12
      // Reduce mass scaling for event bodies to feel heavier
      const massScale = isEvent ? 0.6 : 1
      const mass = (body.mass || 1) * massScale
      ;(Matter as any).Sleeping?.set?.(body, false)
      Matter.Body.applyForce(body, body.position, { x: jitterX * mass, y: -magnitude * mass })

      // Cap upward velocity so they don't over-bounce
      if (body.velocity.y < -4) {
        Matter.Body.setVelocity(body, { x: body.velocity.x, y: -4 })
      }
    }
  }, [scrollY])

  return (
    <div ref={containerRef} className="bg-system-tilebg relative h-full w-full overflow-hidden">
      <canvas
        data-layer="eventsInteraction"
        ref={canvasRef}
        className="absolute top-0 left-0 h-full w-full"
        style={{
          width: canvasSize.width,
          height: canvasSize.height,
        }}
      />
    </div>
  )
}

export default EventsPhysicsCanvas
